import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Download, Mail, MessageSquare, RefreshCw, BookOpen, Users2, Share2, KeyRound, X, UserCircle, FileText, Send, Trash2 } from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";
import { Logo } from "../components/Logo";
import CatalogManager from "../components/CatalogManager";
import SocialManager from "../components/SocialManager";
import AboutManager from "../components/AboutManager";
import NewsletterManager from "../components/NewsletterManager";
import LoginActivity from "../components/LoginActivity";
import { formatAdminDateTime } from "../utils/adminDateTime";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "digsby_admin_token";
const EMAIL_KEY = "digsby_admin_email";

function LoginView({ onLogin }) {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(EMAIL_KEY, data.email || email);
      toast.success("Welcome back!");
      onLogin();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/forgot-password`, { email });
      setSent(true);
      toast.success(data.message || "Reset link sent.");
    } catch {
      toast.error("Could not send reset link. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FFFBF2] px-4">
      <div className="w-full max-w-md bg-white rounded-[2rem] border-2 border-black/10 shadow-sm p-8 sm:p-10">
        <div className="flex justify-center mb-6"><Logo height={70} /></div>

        {mode === "login" ? (
          <form onSubmit={submit} data-testid="admin-login-form">
            <div className="flex items-center gap-2 justify-center mb-6">
              <Lock size={18} className="text-[#0000FF]" />
              <h1 className="font-heading font-bold text-2xl">Admin Login</h1>
            </div>
            <label htmlFor="admin-email" className="block font-heading font-bold mb-2">Email</label>
            <input
              id="admin-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-email"
              placeholder="admin@digsbyearlylearning.com"
              className="w-full min-h-[52px] rounded-2xl border-2 border-black/15 px-5 font-medium outline-none focus:border-[#0000FF] mb-4"
            />
            <label htmlFor="admin-password" className="block font-heading font-bold mb-2">Password</label>
            <input
              id="admin-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password"
              placeholder="••••••••"
              className="w-full min-h-[52px] rounded-2xl border-2 border-black/15 px-5 font-medium outline-none focus:border-[#0000FF] mb-3"
            />
            <div className="text-right mb-5">
              <button
                type="button"
                onClick={() => { setMode("forgot"); setSent(false); }}
                data-testid="admin-forgot-link"
                className="text-sm font-bold text-[#0000FF] hover:underline"
              >
                Forgot password?
              </button>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full min-h-[52px] rounded-full bg-[#FF0000] text-white font-bold hover:bg-[#d60000] transition-colors disabled:opacity-60"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>
        ) : sent ? (
          <div className="text-center" data-testid="forgot-sent">
            <p className="font-heading font-bold text-xl mb-2">Check your email</p>
            <p className="text-slate-600 mb-6">
              If that email matches the admin account, we've sent a password reset link. It expires in 60 minutes.
            </p>
            <button
              type="button"
              onClick={() => { setMode("login"); setSent(false); }}
              data-testid="forgot-back-login"
              className="text-[#0000FF] font-bold hover:underline"
            >
              Back to login
            </button>
          </div>
        ) : (
          <form onSubmit={submitForgot} data-testid="forgot-form">
            <h1 className="font-heading font-bold text-2xl text-center mb-2">Forgot password?</h1>
            <p className="text-slate-600 text-center mb-6 text-sm">
              Enter your admin email and we'll send you a link to reset it.
            </p>
            <label htmlFor="forgot-email" className="block font-heading font-bold mb-2">Email</label>
            <input
              id="forgot-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="forgot-email"
              placeholder="admin@digsbyearlylearning.com"
              className="w-full min-h-[52px] rounded-2xl border-2 border-black/15 px-5 font-medium outline-none focus:border-[#0000FF] mb-6"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="forgot-submit"
              className="w-full min-h-[52px] rounded-full bg-[#FF0000] text-white font-bold hover:bg-[#d60000] transition-colors disabled:opacity-60"
            >
              {loading ? "Sending..." : "Send Reset Link"}
            </button>
            <div className="text-center mt-4">
              <button
                type="button"
                onClick={() => setMode("login")}
                className="text-sm text-slate-500 hover:text-[#0000FF] font-medium"
              >
                Back to login
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function formatDate(iso) {
  return formatAdminDateTime(iso);
}

function ChangePasswordModal({ authHeader, onClose, onAuthError }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("New password must be at least 8 characters.");
    if (next !== confirm) return toast.error("New passwords do not match.");
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/change-password`,
        { current_password: current, new_password: next },
        authHeader
      );
      toast.success(data.message || "Password updated.");
      onClose();
    } catch (err) {
      if (err.response?.status === 401) return onAuthError();
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not update password.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4" data-testid="change-password-modal">
      <div className="w-full max-w-md bg-white rounded-[2rem] border-2 border-black/10 shadow-lg p-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <KeyRound size={18} className="text-[#0000FF]" />
            <h2 className="font-heading font-bold text-xl">Change Password</h2>
          </div>
          <button onClick={onClose} data-testid="change-password-close" className="text-slate-400 hover:text-black"><X size={22} /></button>
        </div>
        <form onSubmit={submit}>
          <label className="block font-heading font-bold mb-2">Current password</label>
          <input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)}
            data-testid="cp-current" placeholder="••••••••"
            className="w-full min-h-[48px] rounded-2xl border-2 border-black/15 px-5 font-medium outline-none focus:border-[#0000FF] mb-4" />
          <label className="block font-heading font-bold mb-2">New password</label>
          <input type="password" required value={next} onChange={(e) => setNext(e.target.value)}
            data-testid="cp-new" placeholder="At least 8 characters"
            className="w-full min-h-[48px] rounded-2xl border-2 border-black/15 px-5 font-medium outline-none focus:border-[#0000FF] mb-4" />
          <label className="block font-heading font-bold mb-2">Confirm new password</label>
          <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
            data-testid="cp-confirm" placeholder="Re-enter new password"
            className="w-full min-h-[48px] rounded-2xl border-2 border-black/15 px-5 font-medium outline-none focus:border-[#0000FF] mb-6" />
          <button type="submit" disabled={saving} data-testid="cp-submit"
            className="w-full min-h-[50px] rounded-full bg-[#00A000] text-white font-bold hover:bg-[#008000] transition-colors disabled:opacity-60">
            {saving ? "Updating…" : "Update Password"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [tab, setTab] = useState("contacts");
  const [section, setSection] = useState("leads");
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [adminEmail, setAdminEmail] = useState(localStorage.getItem(EMAIL_KEY) || "");
  const [data, setData] = useState({ contacts: [], subscribers: [], counts: { contacts: 0, subscribers: 0 } });
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem(TOKEN_KEY);
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    axios
      .get(`${API}/api/admin/me`, authHeader)
      .then(({ data }) => {
        if (data.email) {
          setAdminEmail(data.email);
          localStorage.setItem(EMAIL_KEY, data.email);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/leads`, authHeader);
      setData(data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        onLogout();
      } else {
        toast.error("Failed to load leads.");
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deleteContact = async (c) => {
    if (!window.confirm(`Delete the message from "${c.name}" (${c.email})? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/api/admin/contacts/${c.id}`, authHeader);
      setData((d) => ({
        ...d,
        contacts: d.contacts.filter((x) => x.id !== c.id),
        counts: { ...d.counts, contacts: d.counts.contacts - 1 },
      }));
      toast.success("Contact message deleted.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete contact message.");
    }
  };

  const deleteSubscriber = async (s) => {
    if (!window.confirm(`Delete subscriber "${s.email}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/api/admin/subscribers/${s.id}`, authHeader);
      setData((d) => ({
        ...d,
        subscribers: d.subscribers.filter((x) => x.id !== s.id),
        counts: { ...d.counts, subscribers: d.counts.subscribers - 1 },
      }));
      toast.success("Subscriber deleted.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete subscriber.");
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  const exportCsv = async (kind) => {
    try {
      const res = await axios.get(`${API}/api/admin/export/${kind}`, {
        ...authHeader,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = kind === "contacts" ? "digsby_contacts.csv" : "digsby_newsletter.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded.");
    } catch {
      toast.error("Export failed.");
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF2]">
      {showChangePassword && (
        <ChangePasswordModal authHeader={authHeader} onClose={() => setShowChangePassword(false)} onAuthError={onLogout} />
      )}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b-2 border-black/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between">
          <Logo height={52} />
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center bg-[#FFFBF2] rounded-full border-2 border-black/10 p-1 mr-2">
              <button onClick={() => setSection("leads")} data-testid="section-leads"
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold ${section === "leads" ? "bg-[#0000FF] text-white" : "text-slate-600"}`}>
                <Users2 size={16} /> Leads
              </button>
              <button onClick={() => setSection("catalog")} data-testid="section-catalog"
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold ${section === "catalog" ? "bg-[#0000FF] text-white" : "text-slate-600"}`}>
                <BookOpen size={16} /> Catalog
              </button>
              <button onClick={() => setSection("social")} data-testid="section-social"
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold ${section === "social" ? "bg-[#0000FF] text-white" : "text-slate-600"}`}>
                <Share2 size={16} /> Social
              </button>
              <button onClick={() => setSection("about")} data-testid="section-about"
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold ${section === "about" ? "bg-[#0000FF] text-white" : "text-slate-600"}`}>
                <FileText size={16} /> About
              </button>
              <button onClick={() => setSection("newsletter")} data-testid="section-newsletter"
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold ${section === "newsletter" ? "bg-[#0000FF] text-white" : "text-slate-600"}`}>
                <Send size={16} /> Newsletter
              </button>
            </div>
            {adminEmail && (
              <span
                data-testid="admin-current-account"
                title={`Signed in as ${adminEmail}`}
                className="hidden md:inline-flex items-center gap-2 min-h-[44px] rounded-full bg-[#0000FF]/5 border-2 border-[#0000FF]/20 px-4 text-sm font-bold text-[#0000FF] max-w-[240px]"
              >
                <UserCircle size={16} className="shrink-0" />
                <span className="truncate">{adminEmail}</span>
              </span>
            )}
            {section === "leads" && (
              <button
                onClick={load}
                data-testid="admin-refresh"
                className="inline-flex items-center gap-2 min-h-[44px] rounded-full border-2 border-black/15 px-4 font-bold hover:bg-[#FBEF14] transition-colors"
              >
                <RefreshCw size={16} /> Refresh
              </button>
            )}
            <button
              onClick={() => setShowChangePassword(true)}
              data-testid="admin-change-password"
              className="inline-flex items-center gap-2 min-h-[44px] rounded-full border-2 border-black/15 px-4 font-bold hover:bg-[#FBEF14] transition-colors"
            >
              <KeyRound size={16} /> <span className="hidden sm:inline">Password</span>
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 min-h-[44px] rounded-full bg-black text-white px-4 font-bold hover:bg-black/80 transition-colors"
            >
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
        <div className="sm:hidden max-w-6xl mx-auto px-4 pb-3 flex gap-2">
          <button onClick={() => setSection("leads")} className={`flex-1 rounded-full px-4 py-2 text-sm font-bold ${section === "leads" ? "bg-[#0000FF] text-white" : "bg-[#FFFBF2] text-slate-600 border-2 border-black/10"}`}>Leads</button>
          <button onClick={() => setSection("catalog")} className={`flex-1 rounded-full px-4 py-2 text-sm font-bold ${section === "catalog" ? "bg-[#0000FF] text-white" : "bg-[#FFFBF2] text-slate-600 border-2 border-black/10"}`}>Catalog</button>
          <button onClick={() => setSection("social")} className={`flex-1 rounded-full px-4 py-2 text-sm font-bold ${section === "social" ? "bg-[#0000FF] text-white" : "bg-[#FFFBF2] text-slate-600 border-2 border-black/10"}`}>Social</button>
          <button onClick={() => setSection("about")} className={`flex-1 rounded-full px-4 py-2 text-sm font-bold ${section === "about" ? "bg-[#0000FF] text-white" : "bg-[#FFFBF2] text-slate-600 border-2 border-black/10"}`}>About</button>
          <button onClick={() => setSection("newsletter")} className={`flex-1 rounded-full px-4 py-2 text-sm font-bold ${section === "newsletter" ? "bg-[#0000FF] text-white" : "bg-[#FFFBF2] text-slate-600 border-2 border-black/10"}`}>News</button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        {section === "catalog" ? (
          <CatalogManager onAuthError={onLogout} />
        ) : section === "social" ? (
          <SocialManager onAuthError={onLogout} />
        ) : section === "about" ? (
          <AboutManager onAuthError={onLogout} />
        ) : section === "newsletter" ? (
          <NewsletterManager onAuthError={onLogout} />
        ) : (
        <>
        <h1 className="font-heading font-bold text-3xl sm:text-4xl mb-2">Leads Dashboard</h1>
        <p className="text-slate-600 mb-8">Browse and export your captured contact messages and newsletter subscribers.</p>

        <LoginActivity onAuthError={onLogout} />

        {/* Stat cards / tabs */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <button
            onClick={() => setTab("contacts")}
            data-testid="tab-contacts"
            className={`text-left rounded-3xl border-2 p-6 transition-all ${tab === "contacts" ? "border-[#0000FF] bg-white shadow-md" : "border-black/10 bg-white/60"}`}
          >
            <div className="flex items-center gap-2 text-slate-500 font-bold text-sm"><MessageSquare size={16} /> Contact Messages</div>
            <div className="font-heading font-bold text-4xl mt-2">{data.counts.contacts}</div>
          </button>
          <button
            onClick={() => setTab("subscribers")}
            data-testid="tab-subscribers"
            className={`text-left rounded-3xl border-2 p-6 transition-all ${tab === "subscribers" ? "border-[#00A000] bg-white shadow-md" : "border-black/10 bg-white/60"}`}
          >
            <div className="flex items-center gap-2 text-slate-500 font-bold text-sm"><Mail size={16} /> Newsletter Subscribers</div>
            <div className="font-heading font-bold text-4xl mt-2">{data.counts.subscribers}</div>
          </button>
        </div>

        <div className="flex justify-end mb-4">
          <button
            onClick={() => exportCsv(tab === "contacts" ? "contacts" : "newsletter")}
            data-testid="export-csv"
            className="inline-flex items-center gap-2 min-h-[48px] rounded-full bg-[#0000FF] text-white px-6 font-bold hover:bg-[#0000d6] transition-colors"
          >
            <Download size={18} /> Export CSV
          </button>
        </div>

        {loading ? (
          <p className="text-slate-500 py-16 text-center">Loading…</p>
        ) : tab === "contacts" ? (
          <div className="overflow-x-auto rounded-3xl border-2 border-black/10 bg-white" data-testid="contacts-table">
            <table className="w-full text-left text-sm">
              <thead className="bg-[#FFFBF2] border-b-2 border-black/10">
                <tr>
                  <th className="p-4 font-heading">Date</th>
                  <th className="p-4 font-heading">Name</th>
                  <th className="p-4 font-heading">Email</th>
                  <th className="p-4 font-heading">Message</th>
                  <th className="p-4 font-heading">Action</th>
                </tr>
              </thead>
              <tbody>
                {data.contacts.length === 0 ? (
                  <tr><td colSpan={5} className="p-8 text-center text-slate-500">No contact messages yet.</td></tr>
                ) : (
                  data.contacts.map((c, i) => (
                    <tr key={c.id || i} className="border-b border-black/5 align-top">
                      <td className="p-4 whitespace-nowrap text-slate-500">{formatDate(c.created_at)}</td>
                      <td className="p-4 font-medium">{c.name}</td>
                      <td className="p-4"><a href={`mailto:${c.email}`} className="text-[#0000FF] hover:underline">{c.email}</a></td>
                      <td className="p-4 text-slate-700 max-w-md">{c.message}</td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <a
                            href={`mailto:${c.email}?subject=${encodeURIComponent("Re: your message to Digsby Early Learning")}`}
                            data-testid={`contact-reply-${c.id || i}`}
                            className="inline-flex items-center gap-1.5 rounded-full border-2 border-black px-3 py-1.5 text-xs font-bold hover:bg-[#FBEF14] transition-colors whitespace-nowrap"
                          >
                            <Send size={12} /> Reply
                          </a>
                          <button
                            onClick={() => deleteContact(c)}
                            data-testid={`contact-delete-${c.id || i}`}
                            className="inline-flex items-center gap-1.5 rounded-full border-2 border-red-500 text-red-600 px-3 py-1.5 text-xs font-bold hover:bg-red-50 transition-colors whitespace-nowrap"
                          >
                            <Trash2 size={12} /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-3xl border-2 border-black/10 bg-white" data-testid="subscribers-table">
            <table className="w-full text-left text-sm">
              <thead className="bg-[#FFFBF2] border-b-2 border-black/10">
                <tr>
                  <th className="p-4 font-heading">Date</th>
                  <th className="p-4 font-heading">Email</th>
                  <th className="p-4 font-heading">Status</th>
                  <th className="p-4 font-heading">Unsubscribed On</th>
                  <th className="p-4 font-heading">Action</th>
                </tr>
              </thead>
              <tbody>
                {data.subscribers.length === 0 ? (
                  <tr><td colSpan={5} className="p-8 text-center text-slate-500">No subscribers yet.</td></tr>
                ) : (
                  data.subscribers.map((s, i) => (
                    <tr key={s.id || i} className="border-b border-black/5">
                      <td className="p-4 whitespace-nowrap text-slate-500">{formatDate(s.created_at)}</td>
                      <td className="p-4"><a href={`mailto:${s.email}`} className="text-[#0000FF] hover:underline">{s.email}</a></td>
                      <td className="p-4">
                        {s.unsubscribed ? (
                          <span className="rounded-full bg-red-100 text-red-600 px-2.5 py-1 text-xs font-bold">Unsubscribed</span>
                        ) : (
                          <span className="rounded-full bg-green-100 text-green-700 px-2.5 py-1 text-xs font-bold">Active</span>
                        )}
                      </td>
                      <td className="p-4 whitespace-nowrap text-slate-500">{s.unsubscribed_at ? formatDate(s.unsubscribed_at) : "—"}</td>
                      <td className="p-4">
                        <button
                          onClick={() => deleteSubscriber(s)}
                          data-testid={`subscriber-delete-${s.id || i}`}
                          className="inline-flex items-center gap-1.5 rounded-full border-2 border-red-500 text-red-600 px-3 py-1.5 text-xs font-bold hover:bg-red-50 transition-colors whitespace-nowrap"
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
        </>
        )}
      </main>
    </div>
  );
}

export default function Admin() {
  usePageMeta({ title: "Admin | Digsby Early Learning" });
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EMAIL_KEY);
    setAuthed(false);
  };

  return authed ? <Dashboard onLogout={logout} /> : <LoginView onLogin={() => setAuthed(true)} />;
}
